import { useEffect, useMemo, useState } from 'react';
import { PrimaryButton } from '@/shared/ui/PrimaryButton';
import { FormField, fieldInputStyle } from '@/shared/ui/FormField';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { semantic, typography, radius } from '@/shared/theme/tokens';
import { useToast, useApiErrorToast } from '@/shared/ui/Toast';
import { useDeploymentUsers } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import {
  useCreateNotification, useUpdateNotification, useResolvedTemplate, useNotificationEvents,
} from '@/features/notifications/hooks/useNotifications';
import {
  CHANNELS, CHANNEL_ORDER, TRIGGER_TYPES, TRIGGER_ORDER,
  SCHEDULE_FREQUENCIES, WEEKDAYS, validateNotification, emptyNotification, resolvePreview,
  isValidEmail, isValidPhone,
} from '@/features/notifications/notifications.meta';
import { RecipientInput } from '@/features/notifications/ui/RecipientInput';
import { AppDateTimePicker, AppTimePicker } from '@/shared/ui/DateTimeField';
import { toDayjs, timeToDayjs, dayjsToTime } from '@/shared/lib/dateHelpers';
import { AppSelect } from '@/shared/ui/AppSelect';

/**
 * Full-page (in-tab) notification editor. Rendered by NotificationsTab in place of
 * the table; `onClose` returns to the list. Not a modal.
 */
export function NotificationForm({ mode, notification, deploymentId, onClose }) {
  const c = useThemeColors();
  const isDark = useIsDark();
  const toast = useToast();
  const toastApiError = useApiErrorToast();

  const createNotif = useCreateNotification(deploymentId);
  const updateNotif = useUpdateNotification(deploymentId);
  const { data: users = [] } = useDeploymentUsers(deploymentId);

  const [draft, setDraft] = useState(() => notification ? structuredClone(notification) : emptyNotification());
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);

  // Event + channel resolve to exactly one template; picking the event picks it.
  const { template, usingFallback } = useResolvedTemplate(draft.event, draft.channel);
  const { events: eventList } = useNotificationEvents();

  // Keep draft.templateId synced with the resolved template.
  useEffect(() => {
    const id = template?.id ?? null;
    setDraft((d) => (d.templateId === id ? d : { ...d, templateId: id }));
  }, [template]);

  const DEFAULT_SCHEDULE = { frequency: 'daily', days: [], time: '' };

  function set(patch) { setDraft((d) => ({ ...d, ...patch })); }
  function setTrigger(patch) { setDraft((d) => ({ ...d, trigger: { ...d.trigger, ...patch } })); }
  function setSchedule(patch) {
    setDraft((d) => ({ ...d, trigger: { ...d.trigger, schedule: { ...DEFAULT_SCHEDULE, ...d.trigger.schedule, ...patch } } }));
  }
  function selectTriggerType(type) {
    setDraft((d) => ({
      ...d,
      trigger: { ...d.trigger, type, schedule: type === 'recurring' ? (d.trigger.schedule ?? DEFAULT_SCHEDULE) : d.trigger.schedule },
    }));
  }

  // Channel switch resets WA SID for email; template re-resolves via the hook.
  function switchChannel(channel) {
    setDraft((d) => ({ ...d, channel, waSid: channel === 'whatsapp' ? d.waSid : '' }));
  }

  const emailSuggestions = useMemo(
    () => users.filter((u) => u.email).map((u) => ({ value: u.email, label: u.userName ?? u.email })),
    [users],
  );
  // Show ALL users; users without a phone are visible but disabled so the admin
  // sees who would be skipped (and why).
  const phoneSuggestions = useMemo(
    () => users.map((u) => {
      const name = u.userName ?? u.email ?? '(unnamed)';
      return u.phone
        ? { key: u.id, value: u.phone, label: `${name} · ${u.phone}` }
        : { key: u.id, value: '', label: name, disabled: true, note: 'No phone — won’t receive WhatsApp' };
    }),
    [users],
  );

  // Warn about deployment members with no phone (they can't be reached on WhatsApp).
  const phonelessUsers = useMemo(() => users.filter((u) => !u.phone), [users]);

  async function handleSubmit() {
    const errs = validateNotification(draft);
    setErrors(errs);
    if (Object.keys(errs).length) return;

    const recipients = draft.channel === 'whatsapp'
      ? { to: [], cc: [], phones: draft.recipients.phones ?? [] }
      : { to: draft.recipients.to ?? [], cc: draft.recipients.cc ?? [], phones: [] };
    const payload = {
      name: draft.name.trim(),
      description: draft.description.trim(),
      channel: draft.channel,
      event: draft.event,
      templateId: draft.templateId,
      waSid: draft.channel === 'whatsapp' ? draft.waSid.trim() : null,
      recipients,
      trigger: normalizeTrigger(draft.trigger),
    };

    setSaving(true);
    try {
      if (mode === 'edit') {
        await updateNotif.mutateAsync({ id: notification.id, payload });
        toast({ message: 'Notification updated' });
      } else {
        await createNotif.mutateAsync(payload);
        toast({ message: 'Notification created' });
      }
      onClose();
    } catch (err) {
      toastApiError(err);
    } finally {
      setSaving(false);
    }
  }

  const inputStyle = (hasErr) => fieldInputStyle(c, hasErr);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0, fontFamily: typography.fontFamily.body }}>
      {/* Header — back + title + save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button onClick={onClose} title="Back to list" style={{
          width: 34, height: 34, borderRadius: radius.lg, border: `1px solid ${c.border}`, background: c.bg,
          display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', flexShrink: 0,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 18, color: c.textSubtle }}>arrow_back</span>
        </button>
        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={{ margin: 0, fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.heading }}>
            {mode === 'edit' ? 'Edit Notification' : 'New Notification'}
          </h2>
          <p style={{ margin: '2px 0 0', fontSize: typography.fontSize.base, color: c.textSubtle }}>Configure when and how this notification is sent.</p>
        </div>
        <PrimaryButton variant="secondary" size="sm" onClick={onClose} disabled={saving}>Cancel</PrimaryButton>
        <PrimaryButton icon={mode === 'edit' ? 'check' : 'add'} size="sm" onClick={handleSubmit} disabled={saving}>
          {saving ? 'Saving…' : mode === 'edit' ? 'Save changes' : 'Create'}
        </PrimaryButton>
      </div>

      {/* Body (scrolls) — full-width single column */}
      <div style={{ flex: 1, overflowY: 'auto', minHeight: 0 }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, paddingBottom: 8 }}>
            <SectionCard title="Basics" icon="label" c={c}>
              <FormField label="Title" required error={errors.name}>
                <input value={draft.name} onChange={(e) => set({ name: e.target.value })} placeholder="e.g. High-impact recs to plant leads" style={inputStyle(errors.name)} />
              </FormField>
              <FormField label="Description">
                <textarea value={draft.description} onChange={(e) => set({ description: e.target.value })} rows={2} placeholder="Optional — what this notification is for" style={{ ...inputStyle(false), minHeight: 60, resize: 'vertical' }} />
              </FormField>
            </SectionCard>

            <SectionCard title="Delivery medium" icon="send" c={c}>
              <div style={{ display: 'flex', gap: 10 }}>
                {CHANNEL_ORDER.map((key) => {
                  const def = CHANNELS[key];
                  const active = draft.channel === key;
                  return (
                    <button key={key} onClick={() => switchChannel(key)} style={choiceCardStyle(c, active)}>
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: active ? semantic.brandNavyMid : c.textSubtle }}>{def.icon}</span>
                      <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: active ? c.textPrimary : c.textLabel }}>{def.label}</span>
                    </button>
                  );
                })}
              </div>
            </SectionCard>

            {/* Trigger event — picking it picks the template (preview shown). */}
            <SectionCard title="Trigger event & message" icon="bolt" c={c}>
              <FormField error={errors.event} hint="The event that fires this notification. Its message template is shown below.">
                <AppSelect
                  ariaLabel="event"
                  size="sm"
                  placeholder="Select an event"
                  options={eventList.map((ev) => ({ value: ev.key, label: ev.label, group: ev.group }))}
                  value={draft.event || null}
                  onChange={(v) => set({ event: v ?? '' })}
                />
              </FormField>

              {draft.event && template && (
                <div style={{ borderRadius: radius.lg, border: `1px solid ${c.borderSubtle}`, background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle, overflow: 'hidden' }}>
                  <div style={{ padding: '8px 12px', borderBottom: `1px solid ${c.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.widest, textTransform: 'uppercase', color: c.textSubtle, flex: 1 }}>
                      {CHANNELS[draft.channel].label} message preview
                    </span>
                    {usingFallback && (
                      <span style={{ fontSize: typography.fontSize.xs, color: semantic.infoCyan, background: semantic.infoCyanBg, border: `1px solid ${semantic.infoCyanBorder}`, borderRadius: radius.sm, padding: '1px 6px' }}>sample</span>
                    )}
                  </div>
                  <div style={{ padding: 12 }}>
                    {template.subject != null && (
                      <div style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, marginBottom: 8 }}>{resolvePreview(template.subject)}</div>
                    )}
                    <div style={{ fontSize: typography.fontSize.md, color: c.textLabel, whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{resolvePreview(template.body)}</div>
                  </div>
                </div>
              )}
              {draft.event && !template && (
                <div style={{ fontSize: typography.fontSize.base, color: semantic.error }}>No {CHANNELS[draft.channel].label} template exists for this event.</div>
              )}

              {draft.channel === 'whatsapp' && (
                <FormField label="Twilio Messaging SID" required error={errors.waSid} hint="The Twilio Messaging Service SID used to send this WhatsApp template.">
                  <input value={draft.waSid} onChange={(e) => set({ waSid: e.target.value })} placeholder="MGxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxxx" style={inputStyle(errors.waSid)} />
                </FormField>
              )}
            </SectionCard>
            <SectionCard title="Recipients" icon="group" c={c}>
              {draft.channel === 'email' ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                  <FormField label="To" required error={errors.recipients}>
                    <RecipientInput value={draft.recipients.to} onChange={(to) => set({ recipients: { ...draft.recipients, to } })} suggestions={emailSuggestions} validate={isValidEmail} placeholder="Pick a user or type an email" />
                  </FormField>
                  <FormField label="Cc">
                    <RecipientInput value={draft.recipients.cc} onChange={(cc) => set({ recipients: { ...draft.recipients, cc } })} suggestions={emailSuggestions} validate={isValidEmail} placeholder="Optional — add Cc" />
                  </FormField>
                </div>
              ) : (
                <>
                <FormField label="Phone numbers" required error={errors.recipients} hint="Pick a user with a phone on file, or type an E.164 number (e.g. +919812345678).">
                  <RecipientInput value={draft.recipients.phones} onChange={(phones) => set({ recipients: { ...draft.recipients, phones } })} suggestions={phoneSuggestions} validate={isValidPhone} placeholder="Pick a user or type a number, press Enter" />
                </FormField>
                {phonelessUsers.length > 0 && (
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', padding: '8px 10px', borderRadius: radius.md, background: semantic.warningBg, border: `1px solid ${semantic.warningBorder}` }}>
                    <span className="material-symbols-outlined" style={{ fontSize: 16, color: semantic.warning, flexShrink: 0, marginTop: 1 }}>warning</span>
                    <span style={{ fontSize: typography.fontSize.base, color: c.textLabel, lineHeight: 1.45 }}>
                      {phonelessUsers.length} member{phonelessUsers.length === 1 ? '' : 's'} ({phonelessUsers.map((u) => u.userName ?? u.email).join(', ')}) {phonelessUsers.length === 1 ? 'has' : 'have'} no phone number and won’t receive WhatsApp messages. Add a phone in the Users tab.
                    </span>
                  </div>
                )}
                </>
              )}
            </SectionCard>

            <SectionCard title="When to send" icon="schedule" c={c}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10 }}>
                {TRIGGER_ORDER.map((key) => {
                  const def = TRIGGER_TYPES[key];
                  const active = draft.trigger.type === key;
                  return (
                    <button key={key} onClick={() => selectTriggerType(key)} title={def.description} style={{
                      display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 5, textAlign: 'center',
                      padding: '12px 8px', borderRadius: radius.lg, cursor: 'pointer',
                      background: active ? semantic.brandBg : c.bgSubtle,
                      border: `1.5px solid ${active ? semantic.brandBorderSoft : c.borderInput}`,
                    }}>
                      <span className="material-symbols-outlined" style={{ fontSize: 20, color: active ? semantic.brandNavyMid : c.textSubtle }}>{def.icon}</span>
                      <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: active ? c.textPrimary : c.textLabel }}>{def.label}</span>
                    </button>
                  );
                })}
              </div>

              {draft.trigger.type === 'one_time' && (
                <div style={{ marginTop: 12 }}>
                  <FormField label="Run at" required error={errors.trigger}>
                    <AppDateTimePicker value={toDayjs(draft.trigger.runAt)} onChange={(d) => setTrigger({ runAt: d && d.isValid() ? d.toISOString() : null })} />
                  </FormField>
                </div>
              )}

              {draft.trigger.type === 'recurring' && (() => {
                const sched = draft.trigger.schedule ?? DEFAULT_SCHEDULE;
                return (
                  <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 12 }}>
                    {/* Frequency + time side by side */}
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}>
                      <FormField label="Frequency">
                        <div style={{ display: 'flex', gap: 8 }}>
                          {Object.values(SCHEDULE_FREQUENCIES).map((f) => {
                            const active = sched.frequency === f.key;
                            return (
                              <button key={f.key} onClick={() => setSchedule({ frequency: f.key })} style={{
                                flex: 1, padding: '9px 8px', borderRadius: radius.lg, cursor: 'pointer', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
                                background: active ? semantic.brandBg : c.bgSubtle,
                                border: `1.5px solid ${active ? semantic.brandBorderSoft : c.borderInput}`,
                                color: active ? c.textPrimary : c.textLabel,
                              }}>{f.label}</button>
                            );
                          })}
                        </div>
                      </FormField>
                      <FormField label="Time of day" required error={errors.trigger}>
                        <AppTimePicker value={timeToDayjs(sched.time)} onChange={(d) => setSchedule({ time: dayjsToTime(d) })} />
                      </FormField>
                    </div>

                    {sched.frequency === 'weekly' && (
                      <FormField label="On days">
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {WEEKDAYS.map((d) => {
                            const on = sched.days?.includes(d.key);
                            return (
                              <button key={d.key} onClick={() => {
                                const days = sched.days ?? [];
                                setSchedule({ days: on ? days.filter((x) => x !== d.key) : [...days, d.key] });
                              }} style={{
                                flex: 1, minWidth: 40, height: 34, borderRadius: radius.md, cursor: 'pointer', fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.semibold,
                                background: on ? semantic.brandBg : c.bgSubtle,
                                border: `1.5px solid ${on ? semantic.brandBorderSoft : c.borderInput}`,
                                color: on ? semantic.brandNavyMid : c.textLabel,
                              }}>{d.label}</button>
                            );
                          })}
                        </div>
                      </FormField>
                    )}
                  </div>
                );
              })()}
            </SectionCard>
        </div>
      </div>
    </div>
  );
}

function normalizeTrigger(trigger) {
  if (trigger.type === 'instant') return { type: 'instant', runAt: null, schedule: null };
  if (trigger.type === 'one_time') return { type: 'one_time', runAt: trigger.runAt ?? null, schedule: null };
  const s = trigger.schedule ?? {};
  return {
    type: 'recurring', runAt: null,
    schedule: { frequency: s.frequency ?? 'daily', days: s.frequency === 'weekly' ? (s.days ?? []) : [], time: s.time ?? '' },
  };
}


function SectionCard({ title, icon, c, children }) {
  return (
    <section style={{ borderRadius: radius.xl, border: `1px solid ${c.border}`, background: c.bg, overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 16px', borderBottom: `1px solid ${c.border}`, background: c.bgSubtle }}>
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle }}>{icon}</span>
        <span style={{ fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.bold, letterSpacing: typography.letterSpacing.wider, textTransform: 'uppercase', color: c.textSubtle }}>{title}</span>
      </div>
      <div style={{ padding: '14px 16px', display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>
    </section>
  );
}

function choiceCardStyle(c, active) {
  return {
    flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6,
    padding: '12px 10px', borderRadius: 10, cursor: 'pointer',
    background: active ? semantic.brandBg : c.bgSubtle,
    border: `1.5px solid ${active ? semantic.brandBorderSoft : c.borderInput}`,
  };
}

